import { CheckCircle, PencilSimple } from '@phosphor-icons/react';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { Input } from 'src/components/Input';
import { limitString } from 'src/utils/limitString';

interface EditableTitleProps {
  metadata?: string;
  titlePlaceholder?: string;
  subtitlePlaceholder?: string;
  titleMaxLength?: number;
  subtitleMaxLength?: number;
  containerClassName?: string;
  loading?: boolean;
  titleInputRef?: React.RefObject<HTMLInputElement>;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  name: string;
  description: string;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onSave?: () => void;
}

export const EditableTitle = React.memo(
  ({
    metadata,
    titlePlaceholder = 'Enter title',
    subtitlePlaceholder = 'Enter description',
    titleMaxLength = 50,
    subtitleMaxLength = 50,
    containerClassName = '',
    loading = false,
    titleInputRef,
    isEditing,
    setIsEditing,
    name,
    description,
    onNameChange,
    onDescriptionChange,
    onSave,
  }: EditableTitleProps) => {
    const handleEdit = () => {
      if (onSave && isEditing) {
        onSave();
      }

      if (isEditing) {
        setIsEditing(false);
      } else {
        setIsEditing(true);
      }
    };

    useEffect(() => {
      if (isEditing && titleInputRef?.current) {
        titleInputRef.current.focus();
      }
    }, [isEditing, titleInputRef]);

    return (
      <div
        className={`${isEditing ? 'w-[68vw] md:w-[300px] lg:w-[400px]' : ''} ${containerClassName}`}
      >
        <div>
          <div className="flex flex-row justify-between w-full">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-x-2 w-full">
                {isEditing ? (
                  <Input
                    ref={titleInputRef}
                    value={name}
                    variant="text"
                    className="rounded-none border-b-2 mb-2 focus:border-b-primary w-full"
                    placeholder={titlePlaceholder}
                    onChange={(e) => onNameChange(e.target.value)}
                  />
                ) : (
                  <div className="flex">
                    <h1>
                      {name && name.length > 0
                        ? limitString(name, titleMaxLength)
                        : titlePlaceholder}
                    </h1>

                    <div
                      className={clsx('cursor-pointer')}
                      onClick={handleEdit}
                      style={{
                        display: loading ? 'none' : 'block',
                      }}
                    >
                      <PencilSimple
                        className="text-neutral-700 mt-2 ml-4"
                        size={22}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-x-2 w-full">
                {isEditing ? (
                  <Input
                    value={description}
                    variant="text"
                    className="rounded-none border-b-2 mb-2 focus:border-b-primary w-full"
                    placeholder={subtitlePlaceholder}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                  />
                ) : (
                  <span>
                    {description && description.length > 0
                      ? limitString(description, subtitleMaxLength)
                      : subtitlePlaceholder}
                  </span>
                )}
              </div>
            </div>

            {isEditing && (
              <div
                className={clsx('cursor-pointer', isEditing && 'ml-2')}
                onClick={handleEdit}
                style={{
                  display: loading ? 'none' : 'block',
                }}
              >
                <CheckCircle className="text-neutral-700 mt-2" size={22} />
              </div>
            )}
          </div>
        </div>

        {metadata && (
          <div className="flex text-neutral-600 max-w-[800px] max-h-[100px] overflow-hidden text-ellipsis">
            <div>{metadata}</div>
          </div>
        )}
      </div>
    );
  },
);
